/**
 * Python Code Builder for Pyodide
 * Generates Python code for power system analysis
 */

import type { Node, Connection } from '@/types';
import { isBus, isGenerator, isLine, isLoad, isStorage } from '@/types';
import { findNearestBus } from '@/utils/network';
import { log, LOG_CATEGORIES } from '@/utils/logger';
import {
  pyAssign,
  pyAttr,
  pyAugAssign,
  pyBin,
  pyCall,
  pyCompare,
  pyConst,
  pyDict,
  pyExpr,
  pyFor,
  pyIf,
  pyImport,
  pyList,
  pyName,
  pySub,
  pyTuple,
  renderModule,
} from './ast';
import type { PyExpr, PyStmt } from './ast';

// ============================================================
// TYPES
// ============================================================

/** Options for building power flow code */
export interface PowerFlowCodeOptions {
  /** Include storage units */
  includeStorage?: boolean;
  /** Use optimal power flow instead of power flow */
  useOPF?: boolean;
  /** Network variable name in Python */
  networkVar?: string;
}

/** Options for building network code */
export interface NetworkCodeOptions {
  /** Network variable name */
  networkVar?: string;
  /** Number of snapshots */
  snapshots?: number;
}

// ============================================================
// PYTHON TEMPLATES
// ============================================================

/** Python imports for MangoGrid */
export const MangoGrid_IMPORTS = renderModule([
  pyImport([
    { name: 'MangoGrid' },
    { name: 'json' },
    { name: 'numpy', as: 'np' },
  ]),
]);

/** Network initialization template */
export function networkInitTemplate(
  networkVar: string = 'n',
  snapshots: number = 1
): string {
  const net = pyName(networkVar);
  const init = pyAssign([net], pyCall(pyAttr(pyName('MangoGrid'), 'Network')));
  const snapshotArg = snapshots === 1
    ? pyList([pyConst(0)])
    : pyCall(pyName('range'), [pyConst(snapshots)]);
  const setSnapshots = pyExpr(pyCall(pyAttr(net, 'set_snapshots'), [snapshotArg]));
  return renderModule([init, setSnapshots]);
}

/** Power flow execution template */
export function powerFlowTemplate(networkVar: string = 'n'): string {
  const net = pyName(networkVar);
  const ok = pyName('ok');
  const err = pyName('err');

  return renderModule([
    {
      type: 'Try',
      body: [
        pyExpr(pyCall(pyAttr(net, 'pf'))),
        pyAssign([ok], pyConst(true)),
        pyAssign([err], pyConst(null)),
      ],
      handlers: [
        {
          type: pyName('Exception'),
          name: 'e',
          body: [
            pyAssign([ok], pyConst(false)),
            pyAssign([err], pyCall(pyName('str'), [pyName('e')])),
          ],
        },
      ],
    },
  ]);
}

/** Optimal power flow execution template */
export function optimalPowerFlowTemplate(networkVar: string = 'n'): string {
  const net = pyName(networkVar);
  const ok = pyName('ok');
  const err = pyName('err');

  return renderModule([
    {
      type: 'Try',
      body: [
        pyExpr(pyCall(pyAttr(net, 'lopf'))),
        pyAssign([ok], pyConst(true)),
        pyAssign([err], pyConst(null)),
      ],
      handlers: [
        {
          type: pyName('Exception'),
          name: 'e',
          body: [
            pyAssign([ok], pyConst(false)),
            pyAssign([err], pyCall(pyName('str'), [pyName('e')])),
          ],
        },
      ],
    },
  ]);
}

/** Results extraction template */
export function resultsExtractionTemplate(networkVar: string = 'n'): string {
  const net = pyName(networkVar);
  const ok = pyName('ok');
  const err = pyName('err');
  const res = pyName('res');

  const summary = pyDict([
    { key: pyConst('min_v'), value: pyConst(1.0) },
    { key: pyConst('max_v'), value: pyConst(1.0) },
    { key: pyConst('max_load'), value: pyConst(0) },
    { key: pyConst('total_gen'), value: pyConst(0) },
    { key: pyConst('total_load'), value: pyConst(0) },
  ]);

  const initialRes = pyAssign(
    [res],
    pyDict([
      { key: pyConst('success'), value: ok },
      { key: pyConst('error'), value: err },
      { key: pyConst('buses'), value: pyDict() },
      { key: pyConst('lines'), value: pyDict() },
      { key: pyConst('generators'), value: pyDict() },
      { key: pyConst('summary'), value: summary },
    ])
  );

  const b = pyName('b');
  const l = pyName('l');
  const g = pyName('g');
  const vm = pyName('vm');
  const va = pyName('va');
  const p0 = pyName('p0');
  const p1 = pyName('p1');
  const sNom = pyName('s_nom');
  const loading = pyName('loading');
  const pg = pyName('pg');

  const busesIndex = pyAttr(pyAttr(net, 'buses'), 'index');
  const linesIndex = pyAttr(pyAttr(net, 'lines'), 'index');
  const gensIndex = pyAttr(pyAttr(net, 'generators'), 'index');

  const busesVMCols = pyAttr(pyAttr(pyAttr(net, 'buses_t'), 'v_mag_pu'), 'columns');
  const busesVAngCols = pyAttr(pyAttr(pyAttr(net, 'buses_t'), 'v_ang'), 'columns');
  const linesP0Cols = pyAttr(pyAttr(pyAttr(net, 'lines_t'), 'p0'), 'columns');
  const linesP1Cols = pyAttr(pyAttr(pyAttr(net, 'lines_t'), 'p1'), 'columns');
  const gensPCols = pyAttr(pyAttr(pyAttr(net, 'generators_t'), 'p'), 'columns');

  const busesBody = [
    pyAssign([vm], pyConst(1.0)),
    pyIf(
      pyCompare(b, 'in', busesVMCols),
      [
        pyAssign(
          [vm],
          pyCall(pyName('float'), [
            pySub(
              pyAttr(
                pySub(pyAttr(pyAttr(net, 'buses_t'), 'v_mag_pu'), b),
                'iloc'
              ),
              pyConst(0)
            ),
          ])
        ),
      ]
    ),
    pyAssign([va], pyConst(0.0)),
    pyIf(
      pyCompare(b, 'in', busesVAngCols),
      [
        pyAssign(
          [va],
          pyCall(pyName('float'), [
            pySub(
              pyAttr(
                pySub(pyAttr(pyAttr(net, 'buses_t'), 'v_ang'), b),
                'iloc'
              ),
              pyConst(0)
            ),
          ])
        ),
      ]
    ),
    pyAssign(
      [pySub(pySub(res, pyConst('buses')), b)],
      pyDict([
        { key: pyConst('v_mag_pu'), value: vm },
        { key: pyConst('v_ang'), value: va },
      ])
    ),
    pyAssign(
      [pySub(pySub(res, pyConst('summary')), pyConst('min_v'))],
      pyCall(pyName('min'), [pySub(pySub(res, pyConst('summary')), pyConst('min_v')), vm])
    ),
    pyAssign(
      [pySub(pySub(res, pyConst('summary')), pyConst('max_v'))],
      pyCall(pyName('max'), [pySub(pySub(res, pyConst('summary')), pyConst('max_v')), vm])
    ),
  ];

  const linesBody = [
    pyAssign([p0], pyConst(0)),
    pyIf(
      pyCompare(l, 'in', linesP0Cols),
      [
        pyAssign(
          [p0],
          pyCall(pyName('float'), [
            pySub(
              pyAttr(
                pySub(pyAttr(pyAttr(net, 'lines_t'), 'p0'), l),
                'iloc'
              ),
              pyConst(0)
            ),
          ])
        ),
      ]
    ),
    pyAssign([
      sNom,
    ], pySub(pyAttr(pyAttr(net, 'lines'), 'loc'), pyTuple([l, pyConst('s_nom')]))),
    pyAssign([p1], pyConst(0)),
    pyIf(
      pyCompare(l, 'in', linesP1Cols),
      [
        pyAssign(
          [p1],
          pyCall(pyName('float'), [
            pySub(
              pyAttr(
                pySub(pyAttr(pyAttr(net, 'lines_t'), 'p1'), l),
                'iloc'
              ),
              pyConst(0)
            ),
          ])
        ),
      ]
    ),
    pyAssign([loading], pyConst(0)),
    pyIf(
      pyCompare(sNom, '>', pyConst(0)),
      [pyAssign([loading], pyCall(pyName('abs'), [pyBin(pyBin(p0, '/', sNom), '*', pyConst(100))]))]
    ),
    pyAssign(
      [pySub(pySub(res, pyConst('lines')), l)],
      pyDict([
        { key: pyConst('p0'), value: p0 },
        { key: pyConst('p1'), value: p1 },
        { key: pyConst('loading'), value: loading },
      ])
    ),
    pyAssign(
      [pySub(pySub(res, pyConst('summary')), pyConst('max_load'))],
      pyCall(pyName('max'), [pySub(pySub(res, pyConst('summary')), pyConst('max_load')), loading])
    ),
  ];

  const gensBody = [
    pyAssign([pg], pyConst(0)),
    pyIf(
      pyCompare(g, 'in', gensPCols),
      [
        pyAssign(
          [pg],
          pyCall(pyName('float'), [
            pySub(
              pyAttr(
                pySub(pyAttr(pyAttr(net, 'generators_t'), 'p'), g),
                'iloc'
              ),
              pyConst(0)
            ),
          ])
        ),
      ]
    ),
    pyAssign(
      [pySub(pySub(res, pyConst('generators')), g)],
      pyDict([{ key: pyConst('p'), value: pg }])
    ),
    pyAugAssign(pySub(pySub(res, pyConst('summary')), pyConst('total_gen')), '+', pg),
  ];

  const totalLoadAssign = pyAssign([pyName('total_load')], pyConst(0));
  const totalLoadIf = pyIf(
    pyCompare(pyCall(pyName('len'), [pyAttr(net, 'loads')]), '>', pyConst(0)),
    [
      pyAssign(
        [pyName('total_load')],
        pyCall(pyName('float'), [pyCall(pyAttr(pyAttr(pyAttr(net, 'loads'), 'p_set'), 'sum'))])
      ),
    ]
  );
  const totalLoadSummary = pyAssign(
    [pySub(pySub(res, pyConst('summary')), pyConst('total_load'))],
    pyName('total_load')
  );

  const okBlock = pyIf(ok, [
    pyFor(b, busesIndex, busesBody),
    pyFor(l, linesIndex, linesBody),
    pyFor(g, gensIndex, gensBody),
    totalLoadAssign,
    totalLoadIf,
    totalLoadSummary,
  ]);

  const resultsJson = pyAssign(
    [pyName('results_json')],
    pyCall(pyAttr(pyName('json'), 'dumps'), [res])
  );

  const resultsJsonSize = pyAssign(
    [pyName('results_json_size')],
    pyCall(pyName('len'), [pyName('results_json')])
  );

  const resultsSummaryJson = pyAssign(
    [pyName('results_summary_json')],
    pyCall(
      pyAttr(pyName('json'), 'dumps'),
      [
        pyDict([
          { key: pyConst('success'), value: ok },
          { key: pyConst('error'), value: err },
          { key: pyConst('summary'), value: pySub(res, pyConst('summary')) },
        ]),
      ]
    )
  );

  return renderModule([
    initialRes,
    okBlock,
    resultsJson,
    resultsJsonSize,
    resultsSummaryJson,
  ]);
}

// ============================================================
// CODE BUILDERS
// ============================================================

const buildAddComponent = (
  networkVar: string,
  component: string,
  name: string,
  keywords: { arg: string; value: PyExpr }[] = []
): PyStmt => {
  return pyExpr(
    pyCall(
      pyAttr(pyName(networkVar), 'add'),
      [pyConst(component), pyConst(name)],
      keywords
    )
  );
};

/**
 * Get nearest bus name for a node
 */
function getNearestBusName(node: Node, nodes: Node[]): string | null {
  const nearestBus = findNearestBus(node, nodes);
  if (!nearestBus) {
    log.warn(LOG_CATEGORIES.CODEGEN, 'No nearest bus found', { nodeId: node.id, nodeType: node.type });
  }
  return nearestBus?.name || null;
}

/**
 * Build code to add buses
 */
export function buildBusCode(nodes: Node[], networkVar: string = 'n'): string {
  const buses = nodes.filter(isBus);
  const stmts: PyStmt[] = [];

  log.debug(LOG_CATEGORIES.CODEGEN, 'Building bus code', { count: buses.length, networkVar });

  buses.forEach((bus) => {
    const vNom = bus.properties.v_nom || 380;
    stmts.push(
      buildAddComponent(networkVar, 'Bus', bus.name, [
        { arg: 'v_nom', value: pyConst(vNom) },
      ])
    );
  });

  return stmts.length ? renderModule(stmts) : '';
}

/**
 * Build code to add generators
 */
export function buildGeneratorCode(nodes: Node[], networkVar: string = 'n'): string {
  const generators = nodes.filter(isGenerator);
  const stmts: PyStmt[] = [];

  log.debug(LOG_CATEGORIES.CODEGEN, 'Building generator code', { count: generators.length, networkVar });

  generators.forEach((gen) => {
    const busName = getNearestBusName(gen, nodes);
    if (!busName) return;

    const pNom = gen.properties.p_nom || 100;
    const marginalCost = gen.properties.marginal_cost || 0;

    stmts.push(
      buildAddComponent(networkVar, 'Generator', gen.name, [
        { arg: 'bus', value: pyConst(busName) },
        { arg: 'p_nom', value: pyConst(pNom) },
        { arg: 'marginal_cost', value: pyConst(marginalCost) },
      ])
    );
  });

  return stmts.length ? renderModule(stmts) : '';
}

/**
 * Build code to add loads
 */
export function buildLoadCode(nodes: Node[], networkVar: string = 'n'): string {
  const loads = nodes.filter(isLoad);
  const stmts: PyStmt[] = [];

  log.debug(LOG_CATEGORIES.CODEGEN, 'Building load code', { count: loads.length, networkVar });

  loads.forEach((load) => {
    const busName = getNearestBusName(load, nodes);
    if (!busName) return;

    const pSet = load.properties.p_set || 0;

    stmts.push(
      buildAddComponent(networkVar, 'Load', load.name, [
        { arg: 'bus', value: pyConst(busName) },
        { arg: 'p_set', value: pyConst(pSet) },
      ])
    );
  });

  return stmts.length ? renderModule(stmts) : '';
}

/**
 * Build code to add storage units
 */
export function buildStorageCode(nodes: Node[], networkVar: string = 'n'): string {
  const storage = nodes.filter(isStorage);
  const stmts: PyStmt[] = [];

  log.debug(LOG_CATEGORIES.CODEGEN, 'Building storage code', { count: storage.length, networkVar });

  storage.forEach((stor) => {
    const busName = getNearestBusName(stor, nodes);
    if (!busName) return;

    const pNom = stor.properties.p_nom || 50;
    const maxHours = stor.properties.max_hours || 4;

    stmts.push(
      buildAddComponent(networkVar, 'StorageUnit', stor.name, [
        { arg: 'bus', value: pyConst(busName) },
        { arg: 'p_nom', value: pyConst(pNom) },
        { arg: 'max_hours', value: pyConst(maxHours) },
      ])
    );
  });

  return stmts.length ? renderModule(stmts) : '';
}

/**
 * Build code to add lines
 */
export function buildLineCode(
  nodes: Node[],
  connections: Connection[],
  networkVar: string = 'n'
): string {
  const lines = connections.filter(isLine);
  const stmts: PyStmt[] = [];

  log.debug(LOG_CATEGORIES.CODEGEN, 'Building line code', { count: lines.length, networkVar });

  lines.forEach((line) => {
    const fromNode = nodes.find((n) => n.id === line.from);
    const toNode = nodes.find((n) => n.id === line.to);

    if (!fromNode || !toNode) return;

    const r = line.properties.r || 0.01;
    const x = line.properties.x || 0.1;
    const sNom = line.properties.s_nom || 100;

    stmts.push(
      buildAddComponent(networkVar, 'Line', line.id, [
        { arg: 'bus0', value: pyConst(fromNode.name) },
        { arg: 'bus1', value: pyConst(toNode.name) },
        { arg: 'r', value: pyConst(r) },
        { arg: 'x', value: pyConst(x) },
        { arg: 's_nom', value: pyConst(sNom) },
      ])
    );
  });

  return stmts.length ? renderModule(stmts) : '';
}

/**
 * Build complete network code
 */
export function buildNetworkCode(
  nodes: Node[],
  connections: Connection[],
  options: NetworkCodeOptions = {}
): string {
  const { networkVar = 'n', snapshots = 1 } = options;

  log.info(LOG_CATEGORIES.CODEGEN, 'Building network code', {
    nodes: nodes.length,
    connections: connections.length,
    snapshots,
    networkVar,
  });

  let code = MangoGrid_IMPORTS;
  code += networkInitTemplate(networkVar, snapshots);
  code += buildBusCode(nodes, networkVar);
  code += buildGeneratorCode(nodes, networkVar);
  code += buildLoadCode(nodes, networkVar);
  code += buildStorageCode(nodes, networkVar);
  code += buildLineCode(nodes, connections, networkVar);

  return code;
}

/**
 * Build complete power flow code for Pyodide execution
 */
export function buildPowerFlowCode(
  nodes: Node[],
  connections: Connection[],
  options: PowerFlowCodeOptions = {}
): string {
  const { includeStorage = true, useOPF = false, networkVar = 'n' } = options;

  log.info(LOG_CATEGORIES.CODEGEN, 'Building power flow code', {
    nodes: nodes.length,
    connections: connections.length,
    includeStorage,
    useOPF,
    networkVar,
  });

  let code = MangoGrid_IMPORTS;
  code += networkInitTemplate(networkVar, 1);
  code += buildBusCode(nodes, networkVar);
  code += buildGeneratorCode(nodes, networkVar);
  code += buildLoadCode(nodes, networkVar);

  if (includeStorage) {
    code += buildStorageCode(nodes, networkVar);
  }

  code += buildLineCode(nodes, connections, networkVar);

  if (useOPF) {
    code += optimalPowerFlowTemplate(networkVar);
  } else {
    code += powerFlowTemplate(networkVar);
  }

  code += resultsExtractionTemplate(networkVar);

  return code;
}

export default buildPowerFlowCode;

